// ---------------------------------------------------------------------------
// Wave 1 Track E — credit bundles, platform rollup, tenant credits admin
// ---------------------------------------------------------------------------
// Moved verbatim out of aiUsageApi.ts (only exportTenantHistory changed, to
// route through the shared authedBlob helper in ./transport).

import { adminGet, adminSend, authedBlob } from './transport';

// ---------- Credit bundles (read-only mirror of Stripe) ---------------------

// Matches the backend APosAgentCreditBundle shape returned by
// credits-bundles.ts (Stripe products with metadata credit_product=true).
// Only active products are returned, so there is no `active` field.
export interface AgentCreditBundle {
  id: string;
  label: string;
  description?: string;
  creditsPerUnit: number;
  unitPriceUsd: number;
  currency?: string;
  stripePriceId?: string;
}

export async function listCreditBundles(): Promise<AgentCreditBundle[]> {
  // Admin-pool endpoint. The tenant-facing /agents/credits/bundles is
  // authorized against the tenant Cognito pool, so the super-admin token 401s
  // there. Bundles are global; this returns the same { bundles } shape.
  const parsed = await adminGet<{ bundles: AgentCreditBundle[] } | AgentCreditBundle[]>(
    '/agents/admin/credits/bundles',
  );
  return Array.isArray(parsed) ? parsed : (parsed.bundles ?? []);
}

// ---------- Platform credits summary ----------------------------------------

export interface PlatformCreditsRange {
  fromIso?: string;
  toIso?: string;
  preset?: 'today' | '7d' | '30d' | '90d' | 'this_month' | 'last_month' | 'ytd' | 'all_time' | 'custom';
}

export interface PlatformCreditsRollup {
  totals: {
    creditsSoldUsd: number;
    creditsSold: number;
    creditsConsumedUsd: number;
    creditsConsumed: number;
    cogsUsd: number;
    grossMarginUsd: number;
    grossMarginPct: number;
    netOutstandingCredits: number;
  };
  topTenants: Array<{
    tenantId: string;
    tenantName?: string;
    balance: number;
    consumed: number;
    purchased: number;
    marginUsd: number;
  }>;
  recentPurchases: Array<{
    tenantId: string;
    tenantName?: string;
    bundleLabel?: string;
    bundleQuantity?: number;
    credits: number;
    amountUsd: number;
    at: string;
  }>;
}

export async function getPlatformCreditsRollup(range: PlatformCreditsRange): Promise<PlatformCreditsRollup> {
  const params = new URLSearchParams();
  if (range.fromIso) params.set('from', range.fromIso);
  if (range.toIso) params.set('to', range.toIso);
  if (range.preset) params.set('preset', range.preset);
  return adminGet<PlatformCreditsRollup>(`/agents/admin/credits/summary?${params.toString()}`);
}

// ---------- Tenant credits summary ------------------------------------------

// Matches the deployed admin-tenant-summary response. All consumption figures
// are scoped to the requested time range (the calendar). `totals.*Credits`
// loaded/spent fields are additive (newer backend) — narrow with `?.`/`??`.
export interface TenantCreditsSummary {
  tenantId: string;
  balance: number;
  timeRange?: { fromIso: string; toIso: string };
  perApi: Array<{
    api: 'anthropic' | 'google_vertex' | 'google_cse' | string;
    calls: number;
    realCostUsd: number;
    creditsConsumed: number;
    revenueUsd: number;
  }>;
  perOperation: Array<{
    operation: string;
    calls: number;
    credits: number;
  }>;
  totals: {
    calls: number;
    realCostUsd: number;
    credits: number; // consumed credits in range
    revenueUsd: number;
    grossMarginUsd: number;
    grossMarginPercent: number;
    // Range-scoped loaded / spent (additive)
    consumedCredits?: number;
    loadedCredits?: number;
    grantedCredits?: number;
    purchasedCredits?: number;
    deductedCredits?: number;
    refundedCredits?: number;
  };
  /** All-time counters from the BALANCE row (not range-scoped). */
  lifetime?: {
    balance: number;
    totalGranted: number;
    totalPurchased: number;
    totalConsumed: number;
    totalRefunded: number;
    totalDeducted: number;
    totalLoaded: number;
  };
}

export async function getTenantCreditsSummary(
  tenantId: string,
  range: PlatformCreditsRange,
): Promise<TenantCreditsSummary> {
  const params = new URLSearchParams();
  if (range.fromIso) params.set('from', range.fromIso);
  if (range.toIso) params.set('to', range.toIso);
  if (range.preset) params.set('preset', range.preset);
  return adminGet<TenantCreditsSummary>(
    `/agents/admin/credits/tenants/${encodeURIComponent(tenantId)}/summary?${params.toString()}`,
  );
}

// ---------- Tenant transaction history --------------------------------------

export type AgentCreditTransactionType =
  | 'PURCHASE'
  | 'CONSUME'
  | 'REFUND'
  | 'ADMIN_GRANT'
  | 'ADMIN_DEDUCT';

export type AgentCreditOperation =
  | 'analytics_query'
  | 'product_assist'
  | 'description_only'
  | 'image_search_only'
  | 'inventory_forecast';

export interface AgentCreditLedgerRow {
  pk: string;
  sk: string;
  type: AgentCreditTransactionType;
  creditsAmount: number;
  balanceBefore: number;
  balanceAfter: number;
  operation?: AgentCreditOperation;
  costUsd?: number;
  breakdown?: Array<{
    api: 'anthropic' | 'google_vertex' | 'google_cse' | string;
    costUsd: number;
    burnRate: number;
    credits: number;
    detail?: Record<string, unknown>;
  }>;
  stripePaymentIntentId?: string;
  stripeCheckoutSessionId?: string;
  stripeBundleId?: string;
  bundleLabel?: string;
  bundleQuantity?: number;
  creditsPerUnit?: number;
  grantedBy?: string;
  reason?: string;
  refundOfTxnId?: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
  requestId?: string;
}

export interface TenantHistoryFilters {
  fromIso?: string;
  toIso?: string;
  types?: AgentCreditTransactionType[];
  operations?: AgentCreditOperation[];
  limit?: number;
  exclusiveStartKey?: string;
}

export interface TenantHistoryResponse {
  items: AgentCreditLedgerRow[];
  lastEvaluatedKey?: string | null;
  count: number;
}

export async function getTenantTransactionHistory(
  tenantId: string,
  filters: TenantHistoryFilters = {},
  authHeaderOverride?: string,
): Promise<TenantHistoryResponse> {
  const params = new URLSearchParams();
  if (filters.fromIso) params.set('from', filters.fromIso);
  if (filters.toIso) params.set('to', filters.toIso);
  if (filters.types?.length) params.set('types', filters.types.join(','));
  if (filters.operations?.length) params.set('operations', filters.operations.join(','));
  if (filters.limit) params.set('limit', String(filters.limit));
  if (filters.exclusiveStartKey) params.set('exclusiveStartKey', filters.exclusiveStartKey);
  return adminGet<TenantHistoryResponse>(
    `/agents/admin/credits/tenants/${encodeURIComponent(tenantId)}/history?${params.toString()}`,
    authHeaderOverride,
  );
}

export async function exportTenantHistory(
  tenantId: string,
  format: 'csv' | 'pdf',
  filters: TenantHistoryFilters = {},
  authHeaderOverride?: string,
): Promise<Blob> {
  const params = new URLSearchParams();
  params.set('format', format);
  if (filters.fromIso) params.set('from', filters.fromIso);
  if (filters.toIso) params.set('to', filters.toIso);
  if (filters.types?.length) params.set('types', filters.types.join(','));
  if (filters.operations?.length) params.set('operations', filters.operations.join(','));
  // Exports are the crown jewels in a CSV (spec §9.2): super-admin claims are
  // enforced server-side and the server writes an audit row for every call.
  return authedBlob(
    `/agents/admin/credits/tenants/${encodeURIComponent(tenantId)}/export?${params.toString()}`,
    authHeaderOverride,
  );
}

// ---------- Grant / Deduct --------------------------------------------------

export interface GrantCreditsBody {
  credits: number;
  reason?: string;
  idempotencyKey?: string;
}

export interface DeductCreditsBody extends GrantCreditsBody {
  /** Client must echo "DEDUCT" to confirm destructive action. */
  typedConfirmation: 'DEDUCT';
}

export interface GrantDeductResponse {
  balance: number;
  ledgerRowSk: string;
}

export async function grantCredits(tenantId: string, body: GrantCreditsBody): Promise<GrantDeductResponse> {
  return adminSend<GrantDeductResponse>(
    `/agents/admin/credits/tenants/${encodeURIComponent(tenantId)}/grant`,
    'POST',
    body,
  );
}

export async function deductCredits(tenantId: string, body: DeductCreditsBody): Promise<GrantDeductResponse> {
  return adminSend<GrantDeductResponse>(
    `/agents/admin/credits/tenants/${encodeURIComponent(tenantId)}/deduct`,
    'POST',
    body,
  );
}

// ---------- Tenant pricing override -----------------------------------------

export interface TenantPricingApiOverride {
  burnRateOverride?: number;
  free?: boolean;
}

export interface TenantPricingOverride {
  pk: string;
  sk: string;
  minPurchaseCredits?: number | null;
  apis?: {
    anthropic?: TenantPricingApiOverride;
    google_vertex?: TenantPricingApiOverride;
    google_cse?: TenantPricingApiOverride;
  };
  note?: string;
  updatedAt?: string;
  updatedBy?: string;
  auditLog?: Array<{ at: string; by: string; diff: unknown; note?: string }>;
}

export interface TenantPricingResolved {
  apis: Record<
    string,
    {
      burnRate: number;
      minCreditsPerCall: number;
      isFree: boolean;
      isEnabled: boolean;
      source: 'tenant' | 'global';
    }
  >;
  minPurchaseCredits: number;
}

export interface TenantPricingGetResponse {
  override: TenantPricingOverride | null;
  resolved: TenantPricingResolved;
}

export async function getTenantPricing(tenantId: string): Promise<TenantPricingGetResponse> {
  return adminGet<TenantPricingGetResponse>(`/agents/admin/tenants/${encodeURIComponent(tenantId)}/pricing`);
}

export interface TenantPricingPutBody {
  apis?: {
    anthropic?: TenantPricingApiOverride;
    google_vertex?: TenantPricingApiOverride;
    google_cse?: TenantPricingApiOverride;
  };
  minPurchaseCredits?: number | null;
  note?: string;
  /** true = clear override row entirely (reset to global). */
  resetToGlobal?: boolean;
}

export async function updateTenantPricing(tenantId: string, body: TenantPricingPutBody): Promise<TenantPricingGetResponse> {
  return adminSend<TenantPricingGetResponse>(
    `/agents/admin/tenants/${encodeURIComponent(tenantId)}/pricing`,
    'PUT',
    body,
  );
}
